import { useEffect, useState } from 'react';
import { useParams, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { SectionTab, SectionTabs } from '../components/ui/section-tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { useAuth } from '../hooks/useAuth';
import { cn } from '../lib/utils';
import { leaderboardService } from '../services/leaderboard';
import { gymsService } from '../services/gyms';
import { RankTrophy } from '../components/leaderboard/RankTrophy';
import LeaderboardPodium from '../components/leaderboard/LeaderboardPodium';
import type { LeaderboardEntry, LeaderboardClimbBreakdown, LeaderboardGender } from '../types/leaderboard';
import type { Gym } from '../types/gym';

// The gym's whole table first, then one per gender — the same points, ranked
// among the climbers who gave that gender on their profile.
const TABS: { key: 'all' | LeaderboardGender; gender?: LeaderboardGender }[] = [
  { key: 'all' },
  { key: 'male', gender: 'male' },
  { key: 'female', gender: 'female' },
];

// How each send type weighs a climber's share, as GymLeaderboardCalculator does.
const SEND_TYPE_WEIGHTS = ['on_sight', 'flash', 'worked', 'nd'] as const;

function parseGender(value: string | null): LeaderboardGender | undefined {
  return value === 'male' || value === 'female' ? value : undefined;
}

// Backend already rounds to 1 decimal; drop a trailing ".0" for whole numbers.
function formatScore(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export default function GymLeaderboard() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  // In the address, so a table can be shared and survives a reload.
  const [searchParams, setSearchParams] = useSearchParams();
  const gender = parseGender(searchParams.get('gender'));
  const [howPointsOpen, setHowPointsOpen] = useState(false);

  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<LeaderboardEntry | null>(null);
  const [gym, setGym] = useState<Gym | null>(null);

  useEffect(() => {
    if (!slug) return;
    let active = true;
    setLoading(true);
    leaderboardService
      .getGymLeaderboard(slug, gender)
      .then(data => { if (active) { setEntries(data); setError(null); } })
      .catch(err => { if (active) setError(err instanceof Error ? err.message : t('errors.unexpectedError')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug, gender, t]);

  const selectTab = (next?: LeaderboardGender) => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next) params.set('gender', next);
        else params.delete('gender');
        return params;
      },
      { replace: true }
    );
  };

  // Fetch the gym so the breadcrumb can show its logo (and link back to it).
  useEffect(() => {
    if (!slug) return;
    let active = true;
    gymsService
      .getGymBySlug(slug)
      .then(data => { if (active) setGym(data); })
      .catch(() => { if (active) setGym(null); });
    return () => { active = false; };
  }, [slug]);

  useBreadcrumb(
    () => ({
      onBack: () => (slug ? navigate(`/gyms/${slug}`) : navigate(-1)),
      hideHomeLogo: true,
      segments: gym
        ? [
            {
              label: gym.name,
              avatarUrl: gym.logo_small_url || gym.logo_url,
              onClick: () => { if (slug) navigate(`/gyms/${slug}`); },
            },
            { label: t('leaderboard.title') },
          ]
        : [{ label: t('leaderboard.title') }],
    }),
    [slug, gym?.name, gym?.logo_small_url, gym?.logo_url, t]
  );

  const sendTypeLabel = (breakdown: LeaderboardClimbBreakdown): string => {
    const type = breakdown.send_type ?? 'nd';
    if (type === 'worked' && breakdown.attempts_count) {
      return t('leaderboard.sendType.worked_attempts', { count: breakdown.attempts_count });
    }
    return t(`leaderboard.sendType.${type}`);
  };

  if (!slug) return <Navigate to="/not-found" replace />;

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <SectionTabs className="flex-1" data-testid="leaderboard-tabs">
          {TABS.map(tab => (
            <SectionTab key={tab.key} active={gender === tab.gender} onClick={() => selectTab(tab.gender)}>
              {t(`leaderboard.tabs.${tab.key}`)}
            </SectionTab>
          ))}
        </SectionTabs>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setHowPointsOpen(true)}
          aria-label={t('leaderboard.howPoints.open')}
          title={t('leaderboard.howPoints.open')}
          className="shrink-0 text-muted-foreground hover:text-foreground"
          data-testid="leaderboard-how-points"
        >
          <Info className="h-5 w-5" />
        </Button>
      </div>

      {loading ? (
        <Card>
          <CardContent className="p-0">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 border-b last:border-b-0">
                <Skeleton className="h-5 w-5 rounded" />
                <Skeleton className="h-10 w-10 rounded-full" />
                <Skeleton className="h-4 w-32 flex-1" />
                <Skeleton className="h-4 w-10" />
              </div>
            ))}
          </CardContent>
        </Card>
      ) : error ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">{error}</CardContent></Card>
      ) : entries.length === 0 ? (
        <Card><CardContent className="p-12 text-center text-muted-foreground">{t('leaderboard.empty')}</CardContent></Card>
      ) : (
        <>
        <Card>
          <CardContent className="p-6">
            <LeaderboardPodium entries={entries} onSelect={setSelected} />
          </CardContent>
        </Card>
        {entries.length > 3 && (
        <Card>
          <CardContent className="p-0">
            {entries.slice(3).map(entry => {
              const isCurrentUser = !!user && user.slug === entry.user.slug;
              return (
                <button
                  key={entry.user.id}
                  type="button"
                  onClick={() => setSelected(entry)}
                  className={cn(
                    'flex w-full items-center gap-3 border-b px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-muted/50',
                    isCurrentUser && 'bg-primary/5'
                  )}
                  data-testid="leaderboard-row"
                >
                  <div className="flex w-8 shrink-0 items-center justify-center">
                    {entry.rank <= 3 ? (
                      <RankTrophy rank={entry.rank} className="h-5 w-5" />
                    ) : (
                      <span className="text-sm font-semibold text-muted-foreground">{entry.rank}</span>
                    )}
                  </div>
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={entry.user.avatar_small_url ?? undefined} alt={entry.user.username} />
                    <AvatarFallback>{entry.user.username.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">
                      {entry.user.username}
                      {isCurrentUser && <span className="ml-2 text-xs text-primary">({t('leaderboard.you')})</span>}
                    </p>
                  </div>
                  <span className="shrink-0 text-sm font-semibold tabular-nums">{Math.round(entry.total_points)}</span>
                </button>
              );
            })}
          </CardContent>
        </Card>
        )}
        </>
      )}

      <Sheet open={howPointsOpen} onOpenChange={setHowPointsOpen}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>{t('leaderboard.howPoints.title')}</SheetTitle>
            <SheetDescription>{t('leaderboard.howPoints.intro')}</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-3 text-sm">
            <p>{t('leaderboard.howPoints.weighting')}</p>
            <dl className="divide-y rounded-lg border">
              {SEND_TYPE_WEIGHTS.map(type => (
                <div key={type} className="flex items-center justify-between gap-3 px-4 py-2">
                  <dt className="font-medium">{t(`leaderboard.sendType.${type}`)}</dt>
                  <dd className="text-right text-muted-foreground">{t(`leaderboard.howPoints.${type}`)}</dd>
                </div>
              ))}
            </dl>
            <p>{t('leaderboard.howPoints.total')}</p>
            <p className="text-muted-foreground">{t('leaderboard.howPoints.genders')}</p>
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={!!selected} onOpenChange={open => { if (!open) setSelected(null); }}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-3">
              {selected && (
                <Avatar className="h-9 w-9">
                  <AvatarImage src={selected.user.avatar_small_url ?? undefined} alt={selected.user.username} />
                  <AvatarFallback>{selected.user.username.charAt(0).toUpperCase()}</AvatarFallback>
                </Avatar>
              )}
              <span>{selected?.user.username}</span>
            </SheetTitle>
            <SheetDescription className="sr-only">{t('leaderboard.title')}</SheetDescription>
          </SheetHeader>

          {selected && (() => {
            const boulderPts = selected.climbs.filter(b => b.climb.type === 'Boulder').reduce((s, b) => s + b.points, 0);
            const routePts = selected.climbs.filter(b => b.climb.type === 'Route').reduce((s, b) => s + b.points, 0);
            const hasBoth = boulderPts > 0 && routePts > 0;
            if (!hasBoth) return null;
            return (
              <div className="mt-4 flex gap-6 rounded-lg bg-muted/50 px-4 py-2.5 text-sm">
                <div>
                  <span className="text-muted-foreground">{t('leaderboard.climbType.Boulder')}: </span>
                  <span className="font-semibold tabular-nums">{formatScore(boulderPts)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('leaderboard.climbType.Route')}: </span>
                  <span className="font-semibold tabular-nums">{formatScore(routePts)}</span>
                </div>
              </div>
            );
          })()}

          <div className="mt-4 divide-y">
            {selected?.climbs.map(breakdown => (
              <button
                key={breakdown.climb.id}
                type="button"
                onClick={() => {
                  setSelected(null);
                  navigate(`/gyms/${slug}/${breakdown.climb.type === 'Boulder' ? 'boulders' : 'routes'}/${breakdown.climb.slug}`);
                }}
                className="flex w-full items-start justify-between gap-3 py-2.5 text-left transition-colors hover:bg-muted/50"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-medium">{breakdown.climb.name}</p>
                    <span className={cn(
                      'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                      breakdown.climb.type === 'Boulder'
                        ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    )}>
                      {t(`leaderboard.climbType.${breakdown.climb.type}`)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('leaderboard.climbPointsLabel')}: {formatScore(breakdown.base_score)}{' '}
                    <span className="opacity-70">(1000 / {breakdown.senders_count})</span>
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-semibold tabular-nums">+{formatScore(breakdown.points)}</p>
                  <p className="text-xs text-muted-foreground">{sendTypeLabel(breakdown)}</p>
                </div>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
